import { FC, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { EventAttendeeStatus, User } from '@wellesley/openapi'
import { TooltipWrapper } from '@wellesley/ui'
import {
  getDisplayUsername,
  useCurrentUser,
  UserAvatar,
  UserAvatarProps,
} from '@wellesley/user'
import { getUserNameWithDomain } from 'helpers/getUserNameWithDomain'
import addEmojisToText from 'helpers/addEmojisToText'
import { emojisSlice } from 'components/emojis/emojisSlice'
import { getUserImage } from 'helpers/getUserImage'
import { ATTENDEE_STATUSES, parseAttendeeUri } from '../../helpers/Events.utils'

type BaseProps = {
  isAvatar?: boolean
  size?: UserAvatarProps['size']
  status?: EventAttendeeStatus
}

type EventUserProps =
  | (BaseProps & { user: User; uri?: undefined })
  | (BaseProps & { user?: undefined; uri: string })

function getNames(
  user?: User,
  uri?: string,
): {
  name: string
  fullUsername?: string
} {
  let name: string = ''
  let fullUsername: string | undefined = undefined

  try {
    if (user) {
      if (user?.displayName) {
        name = user.displayName
        fullUsername = getDisplayUsername(
          getUserNameWithDomain(user),
        ).toLowerCase()
      } else {
        name = user.username.toLowerCase()
      }
    } else {
      const uriData = parseAttendeeUri(uri)
      name = uriData.username
      fullUsername =
        `${getDisplayUsername(uriData.username)}@${uriData.domain}`.toLowerCase()
    }

    return {
      name,
      fullUsername,
    }
  } catch {
    return {
      name: 'Unknown user',
    }
  }
}

export const EventUser: FC<EventUserProps> = ({
  uri,
  user,
  status,
  size,
  isAvatar,
}) => {
  const { user: currentUser } = useCurrentUser()
  const emojis = useSelector(emojisSlice.selectors.selectEmojis)

  const { name, fullUsername } = useMemo(() => getNames(user, uri), [user, uri])
  const {
    icon: Icon,
    color: statusColor,
    label: statusLabel,
  } = status ? ATTENDEE_STATUSES[status] : {}

  const avatar = (
    <UserAvatar
      size={size}
      name={addEmojisToText(name, emojis)}
      imgUrl={getUserImage({
        user,
        type: 'avatar',
      })}
    />
  )

  if (isAvatar) {
    return avatar
  }

  return (
    <div className="flex flex-1 items-center gap-xs overflow-hidden">
      <TooltipWrapper
        theme="dark"
        size="small"
        triggerClassname="cursor-pointer"
        tooltipText={statusLabel || ''}
        triggerProps={{
          asChild: true,
        }}
      >
        <div className="relative">
          {avatar}
          {Icon && (
            <div className="absolute bottom-none right-none rounded-full bg-white [transform:translate(12%,12%)]">
              <Icon
                size={16}
                fill={`var(${statusColor})`}
                color="var(--white)"
                className="[&>circle]:stroke-0"
              />
            </div>
          )}
        </div>
      </TooltipWrapper>

      <div className="flex flex-col items-start gap-xxxs overflow-hidden text-base">
        <p className="max-w-full overflow-hidden text-ellipsis font-medium text-gray-900">
          {name} {currentUser?.id === user?.id ? '(You)' : ''}
        </p>
        {fullUsername && (
          <p className="max-w-full overflow-hidden text-ellipsis whitespace-nowrap font-normal text-gray-500">
            {fullUsername}
          </p>
        )}
      </div>
    </div>
  )
}
